import React, { useEffect, useState } from 'react';
import { SelectPicker, Table, DOMHelper, Stack, IconButton, Button, Input, Whisper, Tooltip, Divider } from 'rsuite';
import type { SortType } from 'rsuite';
import { BaseErrorModel, PagedResponseModel, PaginationFilterResult } from '@/models/core.model';
import { StoryChapterTask, StoryChapterTaskCalculationTypeList, StoryChapterTaskPaginationFilterRequest, StoryChapterTaskTypeInfo } from '@/models/story-chapter-task.model';
import { getPagedMainStoryChapterTasks } from '@/services/story-chapter-task.service';
import { useNavigate } from 'react-router-dom';
import EditIcon from '@rsuite/icons/Edit';
import { AxiosError } from 'axios';
import { toastErrorMessage } from '@/utils/toasterMessage';
import TablePagination from '@/components/TablePagination';
import PageContent from '@/components/PageContent';

const { Column, HeaderCell, Cell } = Table;
const { getHeight } = DOMHelper;

interface StoryChapterTaskListProps {
  chapterId?: number;
}

const StoryChapterTaskListPage : React.FC<StoryChapterTaskListProps> = ({chapterId}) => {
  const [filterReq, setFilterReq] = useState<StoryChapterTaskPaginationFilterRequest>(new StoryChapterTaskPaginationFilterRequest());
  const [pageRes, setPageRes] = useState<PaginationFilterResult>(new PaginationFilterResult());  
  const [items, setItems] = useState<StoryChapterTask[]>([]);
  const [loading, setLoading] = useState(true);

  const nav = useNavigate();

  useEffect(() => {
    fetchData(filterReq);
  }, [filterReq]);

  const fetchData = async (req: StoryChapterTaskPaginationFilterRequest) => {
    setLoading(true);
    try {
      const finalReq = {
        ...req,
        chapterId: chapterId ?? req.chapterId
      };
      const result: PagedResponseModel<StoryChapterTask> = await getPagedMainStoryChapterTasks(finalReq);
      if (result.success) {
        setItems(result.result);
        setPageRes(pre => ({ 
          ...pre, 
          pageNumber: result.pageNumber, 
          totalRecords: result.totalRecords, 
          pageSize: result.pageSize 
        }));
      }
    } catch (err) {
      setItems([]);
      const errorModel = (err as AxiosError<BaseErrorModel>).response?.data;
      toastErrorMessage(`Error in fetching story chapter tasks:${errorModel?.errorMessage}`);
    }
    finally {
      setLoading(false);
    }
  };
  
  const handleSortColumn = (sortColumn: string, sortType?: SortType) => {
    setFilterReq(pre => ({
      ...pre, 
      orderBy: sortColumn,
      isAscending: sortType == 'asc'
    }));
  };

  return (
    <PageContent>
      <Stack className="table-toolbar" justifyContent="space-between">
        <Button appearance="primary" onClick={() => nav('/stories/chapters/tasks/new', {state:{chapterId}})}>
          Add New Task
        </Button>
        <Stack spacing={6}>
          <SelectPicker
            label="Type"
            data={Object.values(StoryChapterTaskTypeInfo)}
            searchable={false}
            value={filterReq.type ?? null}
            onChange={dataKey => setFilterReq(pre =>({ ...pre, type: dataKey ?? undefined}))}
          />            
          <Input 
            placeholder="Keyword" 
            value={filterReq.keyword ?? ''} 
            onChange={dataKey => setFilterReq(pre => ({...pre, keyword: dataKey}))}
          />
        </Stack>
      </Stack>

      <Table
        height={Math.max(getHeight(window) - 350, 400)}
        data={items}
        sortColumn={filterReq.orderBy}
        sortType={filterReq.isAscending ? 'asc' : 'desc'}
        onSortColumn={handleSortColumn}
        loading={loading}
      >
        <Column flexGrow={0.5} align="center" fullText sortable>
          <HeaderCell>Order</HeaderCell>
          <Cell dataKey="order" />
        </Column>
        <Column flexGrow={2} align="left" fullText sortable>
          <HeaderCell>Name</HeaderCell>
          <Cell dataKey="name" />
        </Column>
        <Column flexGrow={1} align="left" fullText sortable>
          <HeaderCell>Type</HeaderCell>
          <Cell dataKey='type'>
            {(rowData : StoryChapterTask) => StoryChapterTaskTypeInfo[rowData.type].label ?? ''}
          </Cell>
        </Column>
        <Column flexGrow={0.5} align="center" fullText sortable>
          <HeaderCell>Calculation</HeaderCell>
          <Cell dataKey='calculationType'>
            {(rowData : StoryChapterTask) => StoryChapterTaskCalculationTypeList.findLast(t => t.value == rowData.calculationType)?.label ?? ''}
          </Cell>
        </Column>
        <Column flexGrow={0.5} align="center" fullText sortable>
          <HeaderCell>1st Level Amount</HeaderCell>
          <Cell dataKey="firstLevelAmount" />
        </Column>
        <Column flexGrow={0.5} align="center" fullText sortable>
          <HeaderCell>2nd Level Amount</HeaderCell>
          <Cell dataKey="secondLevelAmount" />
        </Column>
        <Column flexGrow={0.5} align="center" fullText sortable>
          <HeaderCell>3rd Level Amount</HeaderCell>
          <Cell dataKey="thirdLevelAmount" />
        </Column>
        <Column flexGrow={0.5} fixed="right" fullText>
          <HeaderCell>...</HeaderCell>
          <Cell style={{ padding: '6px' }}>
            {(rowData: StoryChapterTask) => 
              <Stack spacing={5}>
                <Whisper placement="top" trigger="hover" speaker={<Tooltip>Edit Chapter Task</Tooltip>}>
                  <IconButton onClick={() => nav('/stories/chapters/tasks/' + rowData.id)} icon={<EditIcon />} circle size="sm" />
                </Whisper>
              </Stack>
            }
          </Cell>
        </Column>
      </Table>
      <Divider />
      <TablePagination 
        totalRecords={pageRes.totalRecords}
        pageSize={pageRes.pageSize}
        activePage={pageRes.pageNumber}
        onChangePage={dataKey => setFilterReq(pre => ({...pre, pageNumber: dataKey}))}
        onChangeLimit={dataKey => setFilterReq(pre => ({...pre, pageSize: dataKey}))}
      />
    </PageContent>
  );
};

export default StoryChapterTaskListPage;
